import { SxProps, Theme } from '@mui/material/styles';

import { Dimens } from '~themes';

interface Options {
	/** Render in the highlighted (active/selected) state. */
	active?: boolean;
	/** Indent the row (used for pages nested inside a dropdown). */
	nested?: boolean;
	/** Override element height. */
	height?: string | number;
	/** Override element width. */
	width?: string | number;
}

/**
 * Shared styling for the menu's clickable rows (page links and dropdown
 * headers): muted by default, fully coloured on hover and when active. Icons
 * and the dropdown chevron are targeted uniformly via `& svg`.
 */
const menuRowSx = ({
	active = false,
	nested = false,
	height = Dimens.fixed(36),
	width = '100%',
}: Options = {}): SxProps<Theme> => {
	return (theme: Theme) => {
		const { palette, transitions } = theme;
		// Short fade between the dim and active/hover states.
		const fadeAnimationDuration = transitions.duration.short;
		const fade = transitions.create(['color', 'opacity'], {
			duration: fadeAnimationDuration,
		});
		const litText = { color: palette.textPrimary.main, opacity: 1, transition: fade };
		const litIcon = { color: palette.fgSecondary.main, opacity: 1, transition: fade };
		const dimText = { color: palette.textSecondary.main, opacity: 0.7, transition: fade };
		const dimIcon = { color: palette.fgTertiary.main, opacity: 0.7, transition: fade };

		return {
			width: width,
			height: height,
			gap: Dimens.spacingMd,
			padding: Dimens.spacingMd,
			justifyContent: 'center',
			alignItems: 'center',
			paddingLeft: nested ? Dimens.spacing5xl : Dimens.spacingMd,
			borderRadius: Dimens.radiusMd,
			backgroundColor: active ? palette.bgTertiary.main : 'transparent',
			transition: transitions.create('background-color', {
				duration: fadeAnimationDuration,
			}),
			// `&&` wins over MUI defaults and the row's own ListItemText sx, so the
			// active/hover colors actually apply.
			'&& .MuiListItemText-primary': active ? litText : dimText,
			'&& svg': active ? litIcon : dimIcon,
			'&:hover': {
				backgroundColor: palette.bgTertiary.main,
				opacity: 1,
				'&& .MuiListItemText-primary': litText,
				'&& svg': litIcon,
			},
		};
	};
};

export default menuRowSx;
